import { useTranslation } from "react-i18next";
import { useEffect, type RefObject } from "react";

/** How long the note stays up. */
const VISIBLE_MS = 4000;

interface AlreadyHereBubbleProps {
  /** Above the launcher, unless it sits too close to the top. */
  above: boolean;
  /** Lined up with the launcher's right edge (it sits on the right half). */
  alignRight: boolean;
  /** The launcher and this note: a press anywhere else closes it. */
  containerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
}

/**
 * What tapping Maya says on the AI Assistant page: Maya is already here, in
 * the page's composer (which glows as this opens). Goes away by itself, on
 * Escape, or on a press anywhere else. Physical left/right, like the
 * launcher it is placed against. Screen readers hear it from the launcher's
 * live region instead (one added together with its text isn't announced).
 */
export const AlreadyHereBubble = ({
  above,
  alignRight,
  containerRef,
  onClose,
}: AlreadyHereBubbleProps) => {
  const { t } = useTranslation(["common"]);

  useEffect(() => {
    const timer = window.setTimeout(onClose, VISIBLE_MS);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [containerRef, onClose]);

  return (
    <p
      aria-hidden="true"
      className="animate-rise-in absolute w-max max-w-72 rounded-lg border border-border bg-surface px-3 py-2 text-xs font-medium text-foreground shadow-lg"
      style={{
        ...(above
          ? { bottom: "calc(100% + 12px)" }
          : { top: "calc(100% + 12px)" }),
        ...(alignRight ? { right: 0 } : { left: 0 }),
      }}
    >
      {t("common:maya.already_here")}
    </p>
  );
};
